import React, { useRef, useState, useEffect } from "react";
import moment from "moment";
import { Box, Tooltip, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { InlineIcon } from "@iconify/react";
import { MyTimeCalendarTooltip } from "my-time/components";
import { useMyTimeIcon } from "my-time/hooks";

interface StyledTooltipProps extends TooltipProps {
  className?: string;
}

const StyledTooltip = styled(({ className, ...props }: StyledTooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  "& .MuiTooltip-tooltip": {
    backgroundColor: "#212121",
    borderRadius: "8px",
    padding: "12px 8px",
  },
  "& .MuiTooltip-arrow": {
    color: "#212121",
  },
}));

interface MyTimeCalendarEvent {
  startTime: string | Date;
  endTime: string | Date;
  myTimeType: string;
  timeDataTypeName: string;
  // Add other properties as needed
}

export const MyTimeCalendarWeeklyEvent = ({
  event,
}: {
  event: MyTimeCalendarEvent;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showIcon, setShowIcon] = useState(true);

  const title = `${moment(event.startTime).format("HH:mm")} - ${moment(
    event.endTime,
  ).format("HH:mm")}`;

  const icon = useMyTimeIcon(event);
  let iconColor = `rgba(33, 33, 33, 0.3)`;

  if (
    [
      "WORKING_TIME",
      "WORKING_TIME_ITEM",
      "STAND_BY",
      "LEAVE",
      "ABSENCE",
      "HOMEOFFICE",
      "ON_CALL",
      "OTHER",
    ].includes(event.myTimeType)
  ) {
    iconColor = `rgba(255, 255, 255, 0.3)`;
  }

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const checkHeight = () => {
      const rbcEventElement = element.closest('.rbc-event');
      if (!rbcEventElement) return;

      const heightPercent = parseFloat((rbcEventElement as HTMLElement).style.height);
      if (!isNaN(heightPercent)) {
        setShowIcon(heightPercent > 11);
      }
    };

    const timeout = setTimeout(checkHeight, 100);
    const rbcEventElement = element.closest('.rbc-event');

    const observer = new MutationObserver(checkHeight);
    if (rbcEventElement) {
      observer.observe(rbcEventElement, {
        attributes: true,
        attributeFilter: ["style"],
      });
    }

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, []);

  return (
    <StyledTooltip
      title={
        <MyTimeCalendarTooltip
          event={{
            ...event,
            start: event.startTime,
            end: event.endTime,
          }}
        />
      }
      placement="top"
      arrow
      disableInteractive
    >
      <Box
        ref={containerRef}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={{ height: "100%" }}
      >
        <Box>{event.timeDataTypeName}</Box>
        {showIcon && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
          >
            <InlineIcon
              icon={icon}
              width="60px"
              height="60px"
              style={{
                color: iconColor,
                minWidth: "20px",
              }}
            />
          </Box>
        )}
        <Box>{title}</Box>
      </Box>
    </StyledTooltip>
  );
};

export default MyTimeCalendarWeeklyEvent;
